import { Duration } from "@bufbuild/protobuf";

import React, { useState } from "react";

import {
  Alert,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import {
  AdminRestrictionFeature,
  AdminRestrictionQuota,
  AdminRestrictionTimeWindow,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/admin/v1/restrictions_pb";
import {
  RestrictionFeature,
  RestrictionQuota,
  RestrictionTimeWindow,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";

import { quotaUsage } from "components/org-display/tabs/components/use-org-ai-credit";

// AdminRestriction["restriction"] — the oneof this component switches on.
type RestrictionOneof =
  | { case: "feature"; value: AdminRestrictionFeature }
  | { case: "timeWindow"; value: AdminRestrictionTimeWindow }
  | { case: "quota"; value: AdminRestrictionQuota }
  | { case: "allowList" | "quotaMap"; value: unknown }
  | { case: undefined; value?: undefined };

interface RestrictionValueEditorProps {
  restriction: RestrictionOneof;
  onChange: (restriction: RestrictionOneof) => void;
  disabled?: boolean;
}

const SECONDS_PER_DAY = 86400;

const FeatureEditor = ({
  value,
  onChange,
  disabled,
}: {
  value: AdminRestrictionFeature;
  onChange: (value: AdminRestrictionFeature) => void;
  disabled?: boolean;
}) => {
  const isRestricted = value.userRestriction?.isRestricted ?? false;

  return (
    <FormControlLabel
      control={
        <Switch
          checked={isRestricted}
          disabled={disabled}
          onChange={(e) =>
            onChange(
              new AdminRestrictionFeature({
                userRestriction: new RestrictionFeature({
                  isRestricted: e.target.checked,
                }),
              }),
            )
          }
          data-testid="restriction-feature-switch"
        />
      }
      label={isRestricted ? "Restricted" : "Not restricted"}
    />
  );
};

const TimeWindowEditor = ({
  value,
  onChange,
  disabled,
}: {
  value: AdminRestrictionTimeWindow;
  onChange: (value: AdminRestrictionTimeWindow) => void;
  disabled?: boolean;
}) => {
  const seconds = value.userRestriction?.maxTimeWindow?.seconds;
  const days = seconds !== undefined ? Number(seconds) / SECONDS_PER_DAY : 0;

  const handleChange = (newDays: number) => {
    if (!Number.isFinite(newDays)) return;

    const wholeDays = BigInt(Math.max(0, Math.round(newDays)));
    onChange(
      new AdminRestrictionTimeWindow({
        userRestriction: new RestrictionTimeWindow({
          maxTimeWindow: new Duration({
            seconds: wholeDays * BigInt(SECONDS_PER_DAY),
            nanos: 0,
          }),
        }),
      }),
    );
  };

  return (
    <TextField
      label="Window (days)"
      type="number"
      size="small"
      value={days}
      disabled={disabled}
      onChange={(e) => handleChange(Number(e.target.value))}
      inputProps={{ min: 0, "data-testid": "restriction-timewindow-input" }}
    />
  );
};

// Edits `limit` only. `remaining` and `usage` are recomputed by the restrictor
// on every read, so writing them here changes nothing; every other field —
// `unit`, `resetTime`, `quotaType` — is carried through untouched.
const QuotaEditor = ({
  value,
  onChange,
  disabled,
}: {
  value: AdminRestrictionQuota;
  onChange: (value: AdminRestrictionQuota) => void;
  disabled?: boolean;
}) => {
  const quota = value.userRestriction;
  const limit = quota?.limit ?? 0;
  // Frozen at open: quotaUsage falls back to limit - remaining, and the limit
  // is what the admin is about to change.
  const [usage] = useState(() => quotaUsage(quota));
  const unitLabel = quota?.unit ? ` ${quota.unit}` : "";

  const emit = (newLimit: number) =>
    onChange(
      new AdminRestrictionQuota({
        ...value,
        userRestriction: new RestrictionQuota({ ...quota, limit: newLimit }),
      }),
    );

  // Floors at 1: api-restrictions reads a limit of 0 as "no cap at all", so an
  // admin typing 0 to cut an org off would grant it everything instead.
  const handleLimitChange = (newLimit: number) => {
    if (!Number.isFinite(newLimit)) return;

    emit(Math.max(1, Math.round(newLimit)));
  };

  return (
    <Stack spacing={1}>
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="restriction-quota-usage"
      >
        {`Using ${usage} of ${limit}${unitLabel}.`}
      </Typography>
      <TextField
        label={quota?.unit ? `Limit (${quota.unit})` : "Limit"}
        type="number"
        size="small"
        value={limit}
        disabled={disabled}
        onChange={(e) => handleLimitChange(Number(e.target.value))}
        helperText="The org's total allowance, not an increase on top of its plan. The smallest cap is 1: a limit of 0 means unlimited, not none."
        inputProps={{ min: 1, "data-testid": "restriction-quota-limit-input" }}
      />
    </Stack>
  );
};

// RestrictionValueEditor is the shared write-surface for any admin-editable
// restriction — switches on the AdminRestriction oneof case and renders the
// matching leaf editor. `feature`, `timeWindow` and `quota` are wired today;
// `allowList`/`quotaMap` fall back to an "unsupported" notice rather than a
// silent no-op, so future shapes are additive here without reworking the
// caller.
const RestrictionValueEditor = ({
  restriction,
  onChange,
  disabled,
}: RestrictionValueEditorProps) => {
  switch (restriction.case) {
    case "feature":
      return (
        <FeatureEditor
          value={restriction.value}
          disabled={disabled}
          onChange={(value) => onChange({ case: "feature", value })}
        />
      );
    case "timeWindow":
      return (
        <TimeWindowEditor
          value={restriction.value}
          disabled={disabled}
          onChange={(value) => onChange({ case: "timeWindow", value })}
        />
      );
    case "quota":
      return (
        <QuotaEditor
          value={restriction.value}
          disabled={disabled}
          onChange={(value) => onChange({ case: "quota", value })}
        />
      );
    default:
      return (
        <Alert severity="warning" data-testid="restriction-editor-unsupported">
          Editing this restriction shape isn&apos;t supported yet.
        </Alert>
      );
  }
};

export default RestrictionValueEditor;
export type { RestrictionOneof };
